import { useMemo } from 'react';
import { checkAggregateACL, isWeaveUser } from '@frontend/auth-helpers';
import { CALL_INTEL_TASK_SETTINGS_FF, useIsCallIntelEligibleAccount } from '@frontend/call-intel-core';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { TASK_SETTINGS_PERMISSION } from '../constants';

export const useHasCallIntelTaskSettingAccess = (locationIds?: string[]) => {
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();
  const selectedLocationIds = locationIds ?? scopedLocationIds;
  const { activeLocationIds, isLoading: isLoadingActiveLocationIds } =
    useIsCallIntelEligibleAccount(selectedLocationIds);

  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isTaskSettingsFFEnabled = getFeatureFlagValue(CALL_INTEL_TASK_SETTINGS_FF);
  const { aclValuesByLocationId } = checkAggregateACL(activeLocationIds, TASK_SETTINGS_PERMISSION, 'all');

  const totalLocationsCount = selectedLocationIds.length;
  const activeLocationsCount = activeLocationIds.length;

  const { authorizedLocationIds, isAuthorizedOnAllLocations, authorizedLocationMap } = useMemo(() => {
    const authorizedLocationMap = activeLocationIds.reduce<Record<string, boolean>>((map, locationId) => {
      map[locationId] = isWeaveUser() || !!aclValuesByLocationId[locationId];
      return map;
    }, {});

    const authorizedLocationIds = activeLocationIds.filter((locationId) => authorizedLocationMap[locationId] === true);
    const isAuthorizedOnAllLocations = authorizedLocationIds.length === activeLocationIds.length;

    return { authorizedLocationIds, authorizedLocationMap, isAuthorizedOnAllLocations };
  }, [aclValuesByLocationId, activeLocationIds]);

  const hasAccess = !!isTaskSettingsFFEnabled && !isLoadingActiveLocationIds && authorizedLocationIds.length > 0;

  return {
    isLoading: isLoadingActiveLocationIds,
    activeLocationIds,
    totalLocationsCount,
    activeLocationsCount,
    authorizedLocationIds,
    authorizedLocationMap,
    isAuthorizedOnAllLocations,
    hasAccess,
  };
};
